import type { GameFlow } from "../application/game-flow";
import type { ProgressState } from "../application/progress-state";
import {
  observeProgressMutation,
  type ProgressMutation,
} from "../application/progress-mutation";
import {
  getFoodValueUpgradeView,
  getMovementSpeedUpgradeView,
  getStartingSizeUpgradeView,
  type StartingSizePurchaseStatus,
} from "../application/upgrade-rules";

export type UpgradeShellPurchaseStatus =
  StartingSizePurchaseStatus | "persistence-failed";

export interface UpgradeShellStore {
  getState(): ProgressState;
  purchaseFoodValue(
    expectedLevel: number,
  ): ProgressMutation<UpgradeShellPurchaseStatus>;
  purchaseMovementSpeed(
    expectedLevel: number,
  ): ProgressMutation<UpgradeShellPurchaseStatus>;
  purchaseStartingSize(
    expectedLevel: number,
  ): ProgressMutation<UpgradeShellPurchaseStatus>;
}

export interface UpgradeShellElements {
  readonly backButton: HTMLButtonElement;
  readonly buyButton: HTMLButtonElement;
  readonly coins: HTMLElement;
  readonly cost: HTMLElement;
  readonly currentBenefit: HTMLElement;
  readonly currentLevel: HTMLElement;
  readonly homeScreen: HTMLElement;
  readonly foodValueBuyButton: HTMLButtonElement;
  readonly foodValueCost: HTMLElement;
  readonly foodValueCurrentBenefit: HTMLElement;
  readonly foodValueCurrentLevel: HTMLElement;
  readonly foodValueNextBenefit: HTMLElement;
  readonly foodValueStatus: HTMLElement;
  readonly movementSpeedBuyButton: HTMLButtonElement;
  readonly movementSpeedCost: HTMLElement;
  readonly movementSpeedCurrentBenefit: HTMLElement;
  readonly movementSpeedCurrentLevel: HTMLElement;
  readonly movementSpeedNextBenefit: HTMLElement;
  readonly movementSpeedStatus: HTMLElement;
  readonly nextBenefit: HTMLElement;
  readonly settingsButton: HTMLButtonElement;
  readonly settingsScreen: HTMLElement;
  readonly status: HTMLElement;
}

const coinGuide = "Eat smaller snakes to earn coins.";

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing upgrade shell element: ${id}`);
  }
  return element;
};

const statusCopy = (
  status: UpgradeShellPurchaseStatus,
  upgradeName: "Bigger" | "Faster" | "Yummier",
): string => {
  const copies: Readonly<Record<UpgradeShellPurchaseStatus, string>> = {
    "insufficient-funds": coinGuide,
    "invalid-state": "This upgrade is not available right now.",
    "maximum-level": `${upgradeName} is at maximum level.`,
    "persistence-failed":
      "The upgrade was not saved. Your coins are unchanged.",
    purchased: `${upgradeName} is upgraded and saved.`,
    "stale-command": "That upgrade action was already handled.",
  };
  return copies[status];
};

export class UpgradeShell {
  private homeAvailable = false;
  private mutationPending = false;
  private open = false;
  private readonly unsubscribeFlow: () => void;

  public constructor(
    flow: GameFlow,
    private readonly store: UpgradeShellStore,
    private readonly elements: UpgradeShellElements,
    private readonly renderRelatedContent: () => void = () => undefined,
  ) {
    elements.settingsButton.addEventListener("click", this.handleOpen);
    elements.backButton.addEventListener("click", this.handleBack);
    elements.buyButton.addEventListener("click", this.handlePurchase);
    elements.foodValueBuyButton.addEventListener(
      "click",
      this.handleFoodValuePurchase,
    );
    elements.movementSpeedBuyButton.addEventListener(
      "click",
      this.handleMovementSpeedPurchase,
    );
    this.unsubscribeFlow = flow.subscribe((snapshot) => {
      this.homeAvailable = snapshot.scene === "home";
      if (!this.homeAvailable) {
        this.open = false;
      }
      this.render();
    });
  }

  public destroy(): void {
    this.elements.settingsButton.removeEventListener("click", this.handleOpen);
    this.elements.backButton.removeEventListener("click", this.handleBack);
    this.elements.buyButton.removeEventListener("click", this.handlePurchase);
    this.elements.foodValueBuyButton.removeEventListener(
      "click",
      this.handleFoodValuePurchase,
    );
    this.elements.movementSpeedBuyButton.removeEventListener(
      "click",
      this.handleMovementSpeedPurchase,
    );
    this.unsubscribeFlow();
  }

  private readonly handleBack = (): void => {
    this.open = false;
    this.elements.status.textContent = "";
    this.elements.foodValueStatus.textContent = "";
    this.elements.movementSpeedStatus.textContent = "";
    this.render();
  };

  private readonly handleOpen = (): void => {
    this.open = true;
    this.elements.status.textContent = "";
    this.elements.foodValueStatus.textContent = "";
    this.elements.movementSpeedStatus.textContent = "";
    this.render();
  };

  private readonly handlePurchase = (): void => {
    const view = getStartingSizeUpgradeView(this.store.getState());
    this.observePurchase(
      this.store.purchaseStartingSize(view.currentLevel),
      this.elements.status,
      "Bigger",
    );
  };

  private readonly handleMovementSpeedPurchase = (): void => {
    const view = getMovementSpeedUpgradeView(this.store.getState());
    this.observePurchase(
      this.store.purchaseMovementSpeed(view.currentLevel),
      this.elements.movementSpeedStatus,
      "Faster",
    );
  };

  private readonly handleFoodValuePurchase = (): void => {
    const view = getFoodValueUpgradeView(this.store.getState());
    this.observePurchase(
      this.store.purchaseFoodValue(view.currentLevel),
      this.elements.foodValueStatus,
      "Yummier",
    );
  };

  public render(): void {
    this.elements.homeScreen.hidden = !this.homeAvailable || this.open;
    this.elements.settingsScreen.hidden = !this.homeAvailable || !this.open;
    const view = getStartingSizeUpgradeView(this.store.getState());
    this.elements.coins.textContent = `${String(view.totalCoins)} coins`;
    this.elements.currentLevel.textContent = `Level ${String(view.currentLevel)} of ${String(view.maximumLevel)}`;
    this.elements.currentBenefit.textContent = `Start at snake Level ${String(view.currentBenefit)}.`;
    const maximum = view.nextBenefit === null || view.cost === null;
    this.elements.nextBenefit.textContent = maximum
      ? "Bigger is at maximum level."
      : `Next: start at snake Level ${String(view.nextBenefit)}.`;
    this.elements.cost.textContent = maximum
      ? "Maximum level"
      : `Cost: ${String(view.cost)} coins`;
    this.elements.buyButton.hidden = maximum;
    this.elements.buyButton.disabled = this.mutationPending;

    const movementSpeedView = getMovementSpeedUpgradeView(
      this.store.getState(),
    );
    this.elements.movementSpeedCurrentLevel.textContent = `Level ${String(movementSpeedView.currentLevel)} of ${String(movementSpeedView.maximumLevel)}`;
    this.elements.movementSpeedCurrentBenefit.textContent =
      movementSpeedView.currentLevel === 0
        ? "Your snake has its normal speed."
        : "Your snake moves faster.";
    const movementSpeedMaximum =
      movementSpeedView.nextBenefit === null || movementSpeedView.cost === null;
    this.elements.movementSpeedNextBenefit.textContent = movementSpeedMaximum
      ? "Faster is at maximum level."
      : "Next: your snake moves even faster.";
    this.elements.movementSpeedCost.textContent = movementSpeedMaximum
      ? "Maximum level"
      : `Cost: ${String(movementSpeedView.cost)} coins`;
    this.elements.movementSpeedBuyButton.hidden = movementSpeedMaximum;
    this.elements.movementSpeedBuyButton.disabled = this.mutationPending;

    const foodValueView = getFoodValueUpgradeView(this.store.getState());
    this.elements.foodValueCurrentLevel.textContent = `Level ${String(foodValueView.currentLevel)} of ${String(foodValueView.maximumLevel)}`;
    this.elements.foodValueCurrentBenefit.textContent =
      foodValueView.currentLevel === 0
        ? "Food gives normal growth."
        : "Food helps your snake grow more.";
    const foodValueMaximum =
      foodValueView.nextBenefit === null || foodValueView.cost === null;
    this.elements.foodValueNextBenefit.textContent = foodValueMaximum
      ? "Yummier is at maximum level."
      : "Next: food helps your snake grow even more.";
    this.elements.foodValueCost.textContent = foodValueMaximum
      ? "Maximum level"
      : `Cost: ${String(foodValueView.cost)} coins`;
    this.elements.foodValueBuyButton.hidden = foodValueMaximum;
    this.elements.foodValueBuyButton.disabled = this.mutationPending;
    this.renderRelatedContent();
  }

  private observePurchase(
    mutation: ProgressMutation<UpgradeShellPurchaseStatus>,
    statusElement: HTMLElement,
    upgradeName: "Bigger" | "Faster" | "Yummier",
  ): void {
    observeProgressMutation(mutation, {
      complete: (status) => {
        this.mutationPending = false;
        statusElement.textContent = statusCopy(status, upgradeName);
        this.render();
      },
      failed: () => {
        this.mutationPending = false;
        statusElement.textContent = statusCopy(
          "persistence-failed",
          upgradeName,
        );
        this.render();
      },
      pending: () => {
        this.mutationPending = true;
        statusElement.textContent = `Saving ${upgradeName.toLowerCase()} upgrade…`;
        this.render();
      },
    });
  }
}

export const installUpgradeShell = (
  documentScope: Document,
  flow: GameFlow,
  store: UpgradeShellStore,
  renderRelatedContent?: () => void,
): UpgradeShell =>
  new UpgradeShell(
    flow,
    store,
    {
      backButton: requireElement(
        documentScope,
        "settings-back-button",
      ) as HTMLButtonElement,
      buyButton: requireElement(
        documentScope,
        "starting-size-buy-button",
      ) as HTMLButtonElement,
      coins: requireElement(documentScope, "customize-coins"),
      cost: requireElement(documentScope, "starting-size-cost"),
      currentBenefit: requireElement(
        documentScope,
        "starting-size-current-benefit",
      ),
      currentLevel: requireElement(documentScope, "starting-size-level"),
      homeScreen: requireElement(documentScope, "home-screen"),
      foodValueBuyButton: requireElement(
        documentScope,
        "food-value-buy-button",
      ) as HTMLButtonElement,
      foodValueCost: requireElement(documentScope, "food-value-cost"),
      foodValueCurrentBenefit: requireElement(
        documentScope,
        "food-value-current-benefit",
      ),
      foodValueCurrentLevel: requireElement(documentScope, "food-value-level"),
      foodValueNextBenefit: requireElement(
        documentScope,
        "food-value-next-benefit",
      ),
      foodValueStatus: requireElement(documentScope, "food-value-status"),
      movementSpeedBuyButton: requireElement(
        documentScope,
        "movement-speed-buy-button",
      ) as HTMLButtonElement,
      movementSpeedCost: requireElement(documentScope, "movement-speed-cost"),
      movementSpeedCurrentBenefit: requireElement(
        documentScope,
        "movement-speed-current-benefit",
      ),
      movementSpeedCurrentLevel: requireElement(
        documentScope,
        "movement-speed-level",
      ),
      movementSpeedNextBenefit: requireElement(
        documentScope,
        "movement-speed-next-benefit",
      ),
      movementSpeedStatus: requireElement(
        documentScope,
        "movement-speed-status",
      ),
      nextBenefit: requireElement(documentScope, "starting-size-next-benefit"),
      settingsButton: requireElement(
        documentScope,
        "settings-button",
      ) as HTMLButtonElement,
      settingsScreen: requireElement(documentScope, "settings-screen"),
      status: requireElement(documentScope, "starting-size-status"),
    },
    renderRelatedContent,
  );
